import { FileUploaderDropContainer, FileUploaderItem } from '@carbon/react';
import type { GeometryUpload } from 'types/area-geometry';
import { UPLOAD_ACCEPT, uploadNote } from 'utils/area-geometry-upload';
import styles from './upload-section.module.scss';

interface UploadSectionProps {
  upload: GeometryUpload;
  /** Sends files: the upload's own `add` unless given; the editor makes each drop an undo step. */
  onAdd?: (files: File[]) => void;
  /** Takes one file off: the upload's own `remove` unless given; an undo step in the editor too. */
  onRemove?: (localId: string) => void;
  /** The line over the drop zone. */
  hint?: string;
  /** What the notes call the kept polygons: "verified", or "added" in the editor. */
  kept?: string;
  /** The line once a shape is on the map, if any. */
  shownHint?: string;
}

/**
 * Story 4's method block, as the mock lays it out: a hint, the drop zone, a row
 * per file (a spinner while it is sent and checked, the reason when refused), and
 * what each verified file's check found.
 *
 * @param {UploadSectionProps} props - The upload state.
 * @return {JSX.Element} The upload block.
 */
export function UploadSection({
  upload,
  onAdd = upload.add,
  onRemove = upload.remove,
  hint = 'Only polygon boundaries are kept.',
  kept = 'verified',
  shownHint,
}: UploadSectionProps): JSX.Element {
  const { items, result } = upload;

  return (
    <div className={styles.upload}>
      <p className={styles.hint}>{hint}</p>
      <FileUploaderDropContainer
        accept={UPLOAD_ACCEPT}
        multiple
        labelText="Drag and drop a file here or click to upload"
        name="area-geometry-upload"
        onAddFiles={(
          _event: unknown,
          { addedFiles }: { addedFiles: File[] }
        ) => {
          if (addedFiles.length > 0) onAdd(addedFiles);
        }}
      />
      {items.map(({ file, state, error }) => (
        <FileUploaderItem
          key={file.localId}
          uuid={file.localId}
          name={file.name}
          // 'uploading' shows the spinner; once checked, 'edit' offers the remove.
          status={
            state === 'uploading' || state === 'verifying'
              ? 'uploading'
              : 'edit'
          }
          invalid={state === 'failed'}
          errorSubject="Could not use this file"
          errorBody={error ?? undefined}
          iconDescription="Remove file"
          onDelete={() => onRemove(file.localId)}
        />
      ))}
      {/* Mounted before any note, so a screen reader announces each as it comes. */}
      <div aria-live="polite">
        {items.map(
          ({ file, result: found }) =>
            found && (
              <p key={`note-${file.localId}`} className={styles.hint}>
                {file.name}: {uploadNote(found, kept)}
              </p>
            )
        )}
      </div>
      {shownHint && result && <p className={styles.hint}>{shownHint}</p>}
    </div>
  );
}
